import { useMemo, useState, type CSSProperties, type FormEvent } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { invalidateCustomerMasterCascade } from '@/lib/invalidate-customer-master-cascade';
// 계약 사실화 1단계 정정(26-07-28) — '배치 사유' 문구는 표기 정본 한 벌에서만 온다.
import { CONTRACT_REASON_OPTION_LABEL } from '@/lib/contract-reason';
import {
  DealStage,
  DeploymentReason,
  PricingScheme,
  checkPricingShape,
  type DealSummaryDto,
  type ListDealsResponse,
} from '@bhops/shared';
import {
  contractsApi,
  type CreateContractBody,
  type CreateContractLineBody,
  type CreateContractPricingBody,
} from '@/api/contracts';
import { dealsApi } from '@/api/deals';
import { extractApiError } from '@/api/client';
import { Button, Field, FormError, Input, Select, Textarea } from '@/components/Form';
import { ModelPicker } from '@/components/pickers/ModelPicker';
import { useToast } from '@/components/Toast';
import { kstTodayYmd, ymdToIso } from '@/lib/format-date';
import {
  previousStep,
  tryAdvance,
  type DraftHeader,
  type DraftLine,
  type DraftPricing,
  type WizardStep,
} from './contract-wizard-validation';

// ContractCreateWizard — Phase 5-G2 H4c (design §4.3).
//
// Three-pane wizard for `POST /contracts` (header + lines +
// pricings in one transaction). Pre-fills `dealId` from
// `?dealId=` if present (the path from `DealDetailPage`'s "+
// 새 계약" button); otherwise the operator picks a Deal from
// the eligible list (NEGOTIATION / ACTIVE).
//
// Client-side validation:
//   - Lines: at least one line, modelId+quantity+unitPrice required
//   - Pricings: scheme ↔ amount-shape consistency via the
//     `checkPricingShape` validator lifted in G4 (mirror of the
//     server-side `pricing_scheme_amount_consistent` CHECK)
//
// The server is the single source of truth; this UI just fails
// fast for the obvious cases so operators don't bounce on a
// 400.

// Wizard state shape lives in ./contract-wizard-validation so
// vitest can drive the truth tables without React.

// 계약 사실화 1단계 정정(26-07-28) — 여기 있던 세 번째 라벨 세트
// (REASON_LABEL: '운용 리스'/'금융 리스'/'데모'/'이벤트'/'내부')를 지웠다.
// '배치 사유' 드롭다운은 표기 정본(lib/contract-reason)의 긴 표기
// CONTRACT_REASON_OPTION_LABEL 을 그대로 쓴다 — 마법사에서 '운용 리스'로 저장한
// 줄이 로봇 목록·계약 상세에서는 '구독'으로 보여서, 같은 계약 줄을 두 화면이
// 다른 단어로 부르고 있었다. 이제 머리 단어가 같다('구독(운용리스)' ↔ '구독').

const SCHEME_LABEL: Record<PricingScheme, string> = {
  MONTHLY: '월 단위',
  ONE_TIME: '일시불',
  PARTIAL: '보증금과 월 납부',
};

function emptyLine(today: string): DraftLine {
  return {
    modelId: '',
    quantity: '1',
    reason: DeploymentReason.LEASE_OPERATING,
    unitPriceMonthly: '',
    currency: 'KRW',
    effectiveFrom: today,
    notes: '',
  };
}

function emptyPricing(today: string): DraftPricing {
  return {
    scheme: PricingScheme.MONTHLY,
    totalMonthly: '',
    totalOneTime: '',
    deposit: '',
    currency: 'KRW',
    exchangeRateSnapshot: '1.0',
    effectiveFrom: today,
    paymentSchedule: '',
  };
}

// 추가수정 2차(26-07-27) §2 — '오늘' 기본값을 KST 로. UTC 기준이던 예전
// 코드는 한국 오전 9시 전에 마법사를 열면 계약 시작일이 어제로 박혔다.
function todayIso(): string {
  return kstTodayYmd();
}

// Append two ISO dates / month diff helper. We don't ship a
// general-purpose date util — this is the only call site.
//
// Month-end clamp: naive `setMonth` rolls Jan 31 + 1mo to Mar 3
// (because Feb 31 doesn't exist). We instead clamp the day to the
// last valid day of the target month. Fractional months are
// truncated via Math.floor (matches the original setMonth rounding).
// We also build the YYYY-MM-DD string from local-time getters
// instead of toISOString() to avoid a timezone shift across the
// date boundary.
function addMonths(iso: string, months: number): string {
  if (!iso || !Number.isFinite(months)) return '';
  const date = new Date(iso);
  if (isNaN(date.getTime())) return iso;
  const targetYear = date.getFullYear();
  const targetMonth = date.getMonth() + Math.floor(months);
  const targetDay = date.getDate();
  // Last day of target month (day 0 of next month).
  const lastDayOfTargetMonth = new Date(targetYear, targetMonth + 1, 0).getDate();
  const clampedDay = Math.min(targetDay, lastDayOfTargetMonth);
  const result = new Date(targetYear, targetMonth, clampedDay);
  const y = result.getFullYear();
  const m = String(result.getMonth() + 1).padStart(2, '0');
  const d = String(result.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

// Type-only DealSummaryDto already encodes the stage; widen if
// the server response evolves.
type EligibleDeal = DealSummaryDto;

export function ContractCreateWizard() {
  const navigate = useNavigate();
  const toast = useToast();
  const qc = useQueryClient();
  const [search] = useSearchParams();
  const presetDealId = search.get('dealId') ?? '';
  // Plan v0.3.0 §F-3 follow-up — when launched from
  // CustomerMasterPage's embedded ContractsPage, the link carries
  // `?customerId=...`. We use it to scope the eligible-Deals
  // picker to that customer's deals so the operator doesn't have
  // to scroll a fleet-wide list.
  const presetCustomerId = search.get('customerId') ?? '';

  const today = todayIso();

  const [step, setStep] = useState<WizardStep>('header');
  const [header, setHeader] = useState<DraftHeader>(() => ({
    dealId: presetDealId,
    startDate: today,
    endDate: addMonths(today, 12),
    durationMonths: '12.00',
    renewalScheduledAt: '',
  }));
  const [lines, setLines] = useState<DraftLine[]>(() => [emptyLine(today)]);
  const [pricings, setPricings] = useState<DraftPricing[]>(() => [emptyPricing(today)]);
  const [error, setError] = useState<string | null>(null);

  // Eligible Deals — NEGOTIATION + ACTIVE. The server enforces
  // ownerEntity coherence on POST /contracts, so we just show
  // what the API returns under the user's CASL scope.
  const dealsQ = useQuery<ListDealsResponse>({
    queryKey: ['deals', 'wizard', DealStage.NEGOTIATION, presetCustomerId],
    queryFn: () =>
      dealsApi.list({
        pageSize: 100,
        ...(presetCustomerId ? { customerId: presetCustomerId } : {}),
      }),
    enabled: !presetDealId,
    staleTime: 30_000,
  });

  // The picked Deal — used to render the header summary.
  const pickedDealQ = useQuery<DealSummaryDto>({
    queryKey: ['deals', header.dealId],
    queryFn: () => dealsApi.get(header.dealId),
    enabled: header.dealId !== '',
    staleTime: 30_000,
  });

  // Models query removed — ModelPicker component owns the fetch
  // and shares the cache via the same ['models'] queryKey.

  const create = useMutation({
    mutationFn: (body: CreateContractBody) => contractsApi.create(body),
    onSuccess: (contract) => {
      // round4 #5a (BUG#4) — 계약 생성은 Deal→ACTIVE 전이 + InfraOrder 자동생성 등
      // 교차효과 → cascade 무효화(아니면 방금 만든 계약이 고객사 상세에 '견적' 카드로 stale).
      invalidateCustomerMasterCascade(qc);
      toast.success(`${contract.humanId} 계약을 만들었습니다.`, '계약 생성');
      // 생성 후 고객사 상세로(cascade 로 계약 카드 최신). customerId 없으면 마스터.
      navigate(contract.customerId ? `/customers/${contract.customerId}` : '/business/master');
    },
    onError: (err) => setError(extractApiError(err)),
  });

  // ------ derived ------------------------------------------------

  // Σ row for the lines pane.
  const linesTotal = useMemo(() => {
    let sum = 0n;
    for (const l of lines) {
      const q = BigInt(l.quantity || '0');
      const p = bigFromString(l.unitPriceMonthly);
      if (p === null) continue;
      sum += q * p;
    }
    return sum;
  }, [lines]);

  // Per-pricing-row consistency error from `checkPricingShape`.
  const pricingErrors = useMemo(
    () =>
      pricings.map((p) =>
        checkPricingShape({
          scheme: p.scheme,
          totalMonthly: p.totalMonthly,
          totalOneTime: p.totalOneTime,
          deposit: p.deposit,
        }),
      ),
    [pricings],
  );

  // ------ step transitions ---------------------------------------

  function next(): void {
    setError(null);
    const target = tryAdvance(step, header, lines);
    if (target === null) {
      if (step === 'header') {
        setError('필수 항목을 모두 입력해 주세요. Deal, 시작일, 종료일, 개월 수가 필요합니다.');
      } else if (step === 'lines') {
        setError('각 라인의 모델, 대수, 월 단가, 통화, 유효일을 입력해 주세요.');
      }
      return;
    }
    setStep(target);
  }

  function prev(): void {
    setError(null);
    const target = previousStep(step);
    if (target !== null) {
      // C-3: Reset lines/pricings state for steps after the target so
      // stale validation errors don't bleed back when the operator
      // re-advances after changing header or line data.
      if (target === 'header') {
        setLines([emptyLine(today)]);
        setPricings([emptyPricing(today)]);
      } else if (target === 'lines') {
        setPricings([emptyPricing(today)]);
      }
      setStep(target);
    }
  }

  function submit(e: FormEvent<HTMLFormElement>): void {
    e.preventDefault();
    setError(null);

    // Validate every pricing row before round-tripping.
    for (let i = 0; i < pricings.length; i++) {
      const err = pricingErrors[i];
      if (err) {
        setError(`가격 책정 #${i + 1}: ${err}`);
        return;
      }
    }
    if (pricings.length === 0) {
      setError('가격 책정을 하나 이상 추가해 주세요.');
      return;
    }

    const body: CreateContractBody = {
      dealId: header.dealId,
      // ymdToIso: <input type="date"> 의 YYYY-MM-DD 를 UTC 자정이 아닌
      // 로컬 자정 기준 ISO 로 변환 — UTC-서쪽 타임존에서 캘린더 날짜가
      // 하루 어긋나는 버그 방지.
      startDate: ymdToIso(header.startDate),
      endDate: ymdToIso(header.endDate),
      durationMonths: header.durationMonths,
      renewalScheduledAt: header.renewalScheduledAt
        ? ymdToIso(header.renewalScheduledAt)
        : undefined,
      lines: lines.map<CreateContractLineBody>((l) => ({
        modelId: l.modelId,
        quantity: Number(l.quantity),
        reason: l.reason,
        unitPriceMonthly: l.unitPriceMonthly,
        currency: l.currency,
        effectiveFrom: ymdToIso(l.effectiveFrom),
        notes: l.notes || undefined,
      })),
      pricings: pricings.map<CreateContractPricingBody>((p) => ({
        scheme: p.scheme,
        totalMonthly: p.totalMonthly || null,
        totalOneTime: p.totalOneTime || null,
        deposit: p.deposit || null,
        currency: p.currency,
        exchangeRateSnapshot: p.exchangeRateSnapshot || '1.0',
        effectiveFrom: ymdToIso(p.effectiveFrom),
        paymentSchedule: p.paymentSchedule || undefined,
      })),
    };
    create.mutate(body);
  }

  // ------ render -------------------------------------------------

  const eligibleDeals = (dealsQ.data?.items ?? []).filter(
    (d: EligibleDeal) => d.stage === DealStage.NEGOTIATION || d.stage === DealStage.ACTIVE,
  );

  return (
    <form onSubmit={submit} style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
      <div>
        <Link to="/contracts" className="kuun-caption" style={{ color: 'var(--fg-muted)' }}>
          ← 계약 목록
        </Link>
        <h1 className="kuun-h2" style={{ margin: '4px 0 0' }}>
          새 계약
        </h1>
        <div style={{ marginTop: 8 }} className="kuun-caption">
          {pickedDealQ.data ? (
            <span>
              연결 Deal: <code>{pickedDealQ.data.humanId}</code> ·{' '}
              {pickedDealQ.data.customer.displayName}
            </span>
          ) : (
            <span>연결할 Deal을 먼저 선택해 주세요.</span>
          )}
        </div>
      </div>

      <Stepper current={step} />

      <FormError>{error}</FormError>

      {step === 'header' && (
        <section style={panel}>
          <header style={panelHeader}>1. 헤더</header>
          <div style={{ padding: 16 }}>
            <Field label="연결 Deal" htmlFor="w-deal" required>
              <Select
                id="w-deal"
                value={header.dealId}
                onChange={(e) => setHeader((h) => ({ ...h, dealId: e.target.value }))}
                disabled={Boolean(presetDealId)}
              >
                <option value="">— 선택 —</option>
                {presetDealId && <option value={presetDealId}>{presetDealId.slice(0, 12)}…</option>}
                {eligibleDeals.map((d) => (
                  <option key={d.id} value={d.id}>
                    {d.humanId} · {d.customer.displayName} ({d.stage})
                  </option>
                ))}
              </Select>
              {presetDealId && (
                <span className="kuun-caption" style={{ color: 'var(--fg-muted)' }}>
                  Deal 상세에서 들어와서 바꿀 수 없습니다.
                </span>
              )}
            </Field>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12 }}>
              <Field label="시작일" htmlFor="w-start" required>
                <Input
                  id="w-start"
                  type="date"
                  value={header.startDate}
                  onChange={(e) => {
                    const startDate = e.target.value;
                    setHeader((h) => {
                      // BUG-10 — `Number(x) || 12` falls back to 12
                      // when durationMonths is "0", losing the
                      // operator's intent. Use a finite-number guard
                      // so only NaN / empty strings hit the default.
                      const parsed = Number(h.durationMonths);
                      const months = Number.isFinite(parsed) ? parsed : 12;
                      // BUG-44 — same-day endDate (== startDate) is a
                      // valid 0-day contract; the previous strict `>`
                      // recomputed it. Use `>=` so a still-valid
                      // endDate is preserved.
                      const keepEnd = h.endDate && new Date(h.endDate) >= new Date(startDate);
                      return {
                        ...h,
                        startDate,
                        endDate: keepEnd ? h.endDate : addMonths(startDate, months),
                      };
                    });
                  }}
                />
              </Field>
              <Field label="종료일" htmlFor="w-end" required>
                <Input
                  id="w-end"
                  type="date"
                  value={header.endDate}
                  onChange={(e) => setHeader((h) => ({ ...h, endDate: e.target.value }))}
                />
              </Field>
              <Field
                label="개월 수"
                htmlFor="w-months"
                hint="소수점 둘째 자리까지 적을 수 있습니다. 예: 12.00"
              >
                <Input
                  id="w-months"
                  inputMode="decimal"
                  value={header.durationMonths}
                  onChange={(e) => {
                    const sanitized = e.target.value.replace(/[^0-9.]/g, '');
                    const parsed = Number(sanitized);
                    setHeader((h) => {
                      const next = { ...h, durationMonths: sanitized };
                      // Symmetric counterpart to the startDate handler:
                      // when the operator edits durationMonths, recompute
                      // endDate from startDate + months. Number("12.34.56")
                      // returns NaN → isFinite false → no recompute, so we
                      // never silently fall back to a default value.
                      if (h.startDate && Number.isFinite(parsed) && parsed > 0) {
                        next.endDate = addMonths(h.startDate, parsed);
                      }
                      return next;
                    });
                  }}
                  style={{ fontFamily: 'var(--font-mono)' }}
                />
              </Field>
            </div>

            <Field
              label="재계약 알림일 (선택)"
              htmlFor="w-renewal"
              hint="비워 두면 종료일을 기준으로 자동 계산됩니다"
            >
              <Input
                id="w-renewal"
                type="date"
                value={header.renewalScheduledAt}
                onChange={(e) => setHeader((h) => ({ ...h, renewalScheduledAt: e.target.value }))}
              />
            </Field>
          </div>
        </section>
      )}

      {step === 'lines' && (
        <section style={panel}>
          <header style={panelHeader}>
            <span>2. 계약 라인</span>
            <span className="kuun-caption" style={{ color: 'var(--fg-muted)' }}>
              {lines.length}건, 월 단가에 대수를 곱한 합계 {formatBig(linesTotal)} KRW/월
            </span>
            <Button
              type="button"
              variant="ghost"
              onClick={() => setLines((ls) => [...ls, emptyLine(today)])}
            >
              + 라인 추가
            </Button>
          </header>
          <div style={{ padding: 12, display: 'flex', flexDirection: 'column', gap: 12 }}>
            {lines.map((line, idx) => (
              <div key={idx} style={lineCard}>
                <div style={lineHeaderRow}>
                  <span className="kuun-label">라인 #{idx + 1}</span>
                  {lines.length > 1 && (
                    <Button
                      type="button"
                      variant="ghost"
                      onClick={() => setLines((ls) => ls.filter((_, i) => i !== idx))}
                      style={{ marginLeft: 'auto', color: 'var(--fg-danger)' }}
                    >
                      삭제
                    </Button>
                  )}
                </div>
                <div
                  style={{
                    display: 'grid',
                    gridTemplateColumns: '2fr 1fr 1.2fr 1fr 0.8fr 1fr',
                    gap: 8,
                  }}
                >
                  <Field label="모델" required>
                    <ModelPicker
                      value={line.modelId}
                      onChange={(modelId) =>
                        setLines((ls) => ls.map((l, i) => (i === idx ? { ...l, modelId } : l)))
                      }
                    />
                  </Field>
                  <Field label="대수" required>
                    <Input
                      type="number"
                      min={1}
                      value={line.quantity}
                      onChange={(e) =>
                        setLines((ls) =>
                          ls.map((l, i) =>
                            i === idx
                              ? { ...l, quantity: e.target.value.replace(/[^0-9]/g, '') }
                              : l,
                          ),
                        )
                      }
                    />
                  </Field>
                  <Field label="월 단가 (단위 단가)" required>
                    <Input
                      inputMode="numeric"
                      value={line.unitPriceMonthly}
                      placeholder="500000"
                      onChange={(e) =>
                        setLines((ls) =>
                          ls.map((l, i) =>
                            i === idx
                              ? {
                                  ...l,
                                  unitPriceMonthly: e.target.value.replace(/[^0-9]/g, ''),
                                }
                              : l,
                          ),
                        )
                      }
                      style={{ fontFamily: 'var(--font-mono)' }}
                    />
                  </Field>
                  <Field label="배치 사유" required>
                    <Select
                      value={line.reason}
                      onChange={(e) =>
                        setLines((ls) =>
                          ls.map((l, i) =>
                            i === idx ? { ...l, reason: e.target.value as DeploymentReason } : l,
                          ),
                        )
                      }
                    >
                      {Object.entries(CONTRACT_REASON_OPTION_LABEL).map(([k, v]) => (
                        <option key={k} value={k}>
                          {v}
                        </option>
                      ))}
                    </Select>
                  </Field>
                  <Field label="통화">
                    <Input
                      value={line.currency}
                      maxLength={3}
                      onChange={(e) =>
                        setLines((ls) =>
                          ls.map((l, i) =>
                            i === idx ? { ...l, currency: e.target.value.toUpperCase() } : l,
                          ),
                        )
                      }
                      style={{ fontFamily: 'var(--font-mono)' }}
                    />
                  </Field>
                  <Field label="유효일" required>
                    <Input
                      type="date"
                      value={line.effectiveFrom}
                      onChange={(e) =>
                        setLines((ls) =>
                          ls.map((l, i) =>
                            i === idx ? { ...l, effectiveFrom: e.target.value } : l,
                          ),
                        )
                      }
                    />
                  </Field>
                </div>
                <Field label="메모 (선택)">
                  <Textarea
                    rows={2}
                    value={line.notes}
                    onChange={(e) =>
                      setLines((ls) =>
                        ls.map((l, i) => (i === idx ? { ...l, notes: e.target.value } : l)),
                      )
                    }
                  />
                </Field>
              </div>
            ))}
          </div>
        </section>
      )}

      {step === 'pricing' && (
        <section style={panel}>
          <header style={panelHeader}>
            <span>3. 가격 책정</span>
            {/* 2026-08-31 오너 지적 — 종전엔 DB 제약 이름(pricing_scheme_amount_
                consistent)이 그대로 노출돼 "이 페이지가 뭘 요구하는지 모르겠음"
                이라는 말이 나왔다. 화면은 화면의 말을 쓴다. */}
            <span className="kuun-caption" style={{ color: 'var(--fg-muted)' }}>
              이 계약으로 받을 금액을 적습니다. 고객사 상세와 대시보드의 계약 금액이 여기서
              나옵니다.
            </span>
            <Button
              type="button"
              variant="ghost"
              onClick={() => setPricings((ps) => [...ps, emptyPricing(today)])}
            >
              + 가격책정 추가
            </Button>
          </header>
          <div style={{ padding: 12, display: 'flex', flexDirection: 'column', gap: 12 }}>
            {pricings.map((p, idx) => {
              const rowError = pricingErrors[idx];
              return (
                <div key={idx} style={lineCard}>
                  <div style={lineHeaderRow}>
                    <span className="kuun-label">가격 #{idx + 1}</span>
                    {pricings.length > 1 && (
                      <Button
                        type="button"
                        variant="ghost"
                        onClick={() => setPricings((ps) => ps.filter((_, i) => i !== idx))}
                        style={{ marginLeft: 'auto', color: 'var(--fg-danger)' }}
                      >
                        삭제
                      </Button>
                    )}
                  </div>
                  <div
                    style={{
                      display: 'grid',
                      gridTemplateColumns: '1.2fr 1fr 1fr 1fr',
                      gap: 8,
                    }}
                  >
                    <Field label="받는 방식" required>
                      <Select
                        value={p.scheme}
                        onChange={(e) =>
                          setPricings((ps) =>
                            ps.map((row, i) =>
                              i === idx ? { ...row, scheme: e.target.value as PricingScheme } : row,
                            ),
                          )
                        }
                      >
                        {Object.entries(SCHEME_LABEL).map(([k, v]) => (
                          <option key={k} value={k}>
                            {v}
                          </option>
                        ))}
                      </Select>
                    </Field>
                    <Field
                      label="월 합계"
                      hint={
                        p.scheme === PricingScheme.ONE_TIME
                          ? '일시불 계약에서는 쓰지 않는 칸입니다'
                          : '매달 받는 총액을 원 단위로 적습니다'
                      }
                    >
                      <Input
                        inputMode="numeric"
                        value={p.totalMonthly}
                        disabled={p.scheme === PricingScheme.ONE_TIME}
                        onChange={(e) =>
                          setPricings((ps) =>
                            ps.map((row, i) =>
                              i === idx
                                ? {
                                    ...row,
                                    totalMonthly: e.target.value.replace(/[^0-9]/g, ''),
                                  }
                                : row,
                            ),
                          )
                        }
                        style={{ fontFamily: 'var(--font-mono)' }}
                      />
                    </Field>
                    <Field
                      label="일시불"
                      hint={
                        p.scheme === PricingScheme.MONTHLY
                          ? '월 단위 계약에서는 쓰지 않는 칸입니다'
                          : '한 번에 받는 총액을 원 단위로 적습니다'
                      }
                    >
                      <Input
                        inputMode="numeric"
                        value={p.totalOneTime}
                        disabled={p.scheme === PricingScheme.MONTHLY}
                        onChange={(e) =>
                          setPricings((ps) =>
                            ps.map((row, i) =>
                              i === idx
                                ? {
                                    ...row,
                                    totalOneTime: e.target.value.replace(/[^0-9]/g, ''),
                                  }
                                : row,
                            ),
                          )
                        }
                        style={{ fontFamily: 'var(--font-mono)' }}
                      />
                    </Field>
                    <Field
                      label="보증금"
                      hint={p.scheme === PricingScheme.PARTIAL ? '필수' : '선택'}
                    >
                      <Input
                        inputMode="numeric"
                        value={p.deposit}
                        onChange={(e) =>
                          setPricings((ps) =>
                            ps.map((row, i) =>
                              i === idx
                                ? {
                                    ...row,
                                    deposit: e.target.value.replace(/[^0-9]/g, ''),
                                  }
                                : row,
                            ),
                          )
                        }
                        style={{ fontFamily: 'var(--font-mono)' }}
                      />
                    </Field>
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1.5fr', gap: 8 }}>
                    <Field label="통화">
                      <Input
                        value={p.currency}
                        maxLength={3}
                        onChange={(e) =>
                          setPricings((ps) =>
                            ps.map((row, i) =>
                              i === idx ? { ...row, currency: e.target.value.toUpperCase() } : row,
                            ),
                          )
                        }
                        style={{ fontFamily: 'var(--font-mono)' }}
                      />
                    </Field>
                    <Field label="환율 (KRW=1.0)">
                      <Input
                        inputMode="decimal"
                        value={p.exchangeRateSnapshot}
                        onChange={(e) =>
                          setPricings((ps) =>
                            ps.map((row, i) =>
                              i === idx
                                ? {
                                    ...row,
                                    exchangeRateSnapshot: e.target.value.replace(/[^0-9.]/g, ''),
                                  }
                                : row,
                            ),
                          )
                        }
                        style={{ fontFamily: 'var(--font-mono)' }}
                      />
                    </Field>
                    <Field label="유효일">
                      <Input
                        type="date"
                        value={p.effectiveFrom}
                        onChange={(e) =>
                          setPricings((ps) =>
                            ps.map((row, i) =>
                              i === idx ? { ...row, effectiveFrom: e.target.value } : row,
                            ),
                          )
                        }
                      />
                    </Field>
                  </div>
                  <Field label="결제 일정 (선택)">
                    <Textarea
                      rows={2}
                      value={p.paymentSchedule}
                      onChange={(e) =>
                        setPricings((ps) =>
                          ps.map((row, i) =>
                            i === idx ? { ...row, paymentSchedule: e.target.value } : row,
                          ),
                        )
                      }
                    />
                  </Field>
                  {/* 2026-08-31 오너 지적 — 종전엔 페이지에 들어오자마자(아무것도
                      안 적었는데) 빨간 오류가 떠서 꾸중처럼 읽혔다. 아직 아무
                      금액도 안 적은 행은 "무엇을 적으면 되는지" 안내로, 실제로
                      어긋나게 적은 행만 빨간 오류로 가른다. */}
                  {rowError &&
                    (p.totalMonthly === '' && p.totalOneTime === '' && p.deposit === '' ? (
                      <div className="kuun-caption" style={{ color: 'var(--fg-muted)' }}>
                        {rowError}
                      </div>
                    ) : (
                      <div style={inlineError}>
                        <strong>입력 확인:</strong> {rowError}
                      </div>
                    ))}
                </div>
              );
            })}
          </div>
        </section>
      )}

      <div style={navRow}>
        <Button type="button" variant="secondary" onClick={() => navigate('/contracts')}>
          취소
        </Button>
        {step !== 'header' && (
          <Button type="button" variant="secondary" onClick={prev}>
            ← 이전
          </Button>
        )}
        {step !== 'pricing' && (
          <Button type="button" onClick={next} style={{ marginLeft: 'auto' }}>
            다음 →
          </Button>
        )}
        {step === 'pricing' && (
          <Button
            type="submit"
            disabled={create.isPending || pricingErrors.some((e) => e !== null)}
            style={{ marginLeft: 'auto' }}
          >
            {create.isPending ? '생성 중…' : '계약 생성'}
          </Button>
        )}
      </div>
    </form>
  );
}

function Stepper({ current }: { current: WizardStep }) {
  const steps: Array<{ id: WizardStep; label: string }> = [
    { id: 'header', label: '1. 헤더' },
    { id: 'lines', label: '2. 라인' },
    { id: 'pricing', label: '3. 가격' },
  ];
  const idx = steps.findIndex((s) => s.id === current);
  return (
    <div style={stepperRow}>
      {steps.map((s, i) => {
        const active = i === idx;
        const done = i < idx;
        return (
          <div
            key={s.id}
            style={{
              ...stepCell,
              color: active ? 'var(--fg-default)' : 'var(--fg-muted)',
              fontWeight: active ? 600 : 400,
              borderBottom: active
                ? '2px solid var(--brand)'
                : done
                  ? '2px solid var(--robot-operating)'
                  : '2px solid var(--border-default)',
            }}
          >
            {s.label}
          </div>
        );
      })}
    </div>
  );
}

function bigFromString(s: string): bigint | null {
  if (!s) return null;
  try {
    return BigInt(s);
  } catch {
    return null;
  }
}

function formatBig(n: bigint): string {
  return n.toLocaleString();
}

const panel: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  border: '1px solid var(--border-default)',
  borderRadius: 'var(--r-card)',
  background: 'var(--bg-surface)',
};

const panelHeader: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 8,
  padding: '10px 14px',
  borderBottom: '1px solid var(--border-subtle)',
  fontSize: 13,
  fontWeight: 600,
};

const lineCard: CSSProperties = {
  border: '1px solid var(--border-default)',
  borderRadius: 'var(--r-card)',
  padding: 10,
  background: 'var(--bg-sunken)',
};

const lineHeaderRow: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 8,
  marginBottom: 8,
};

const stepperRow: CSSProperties = {
  display: 'flex',
  borderBottom: '1px solid var(--border-subtle)',
};

const stepCell: CSSProperties = {
  flex: 1,
  padding: '8px 12px',
  textAlign: 'center',
  fontSize: 13,
};

const navRow: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 8,
};

const inlineError: CSSProperties = {
  marginTop: 8,
  padding: '8px 10px',
  borderRadius: 'var(--r-card)',
  fontSize: 12,
  background: 'var(--bg-danger-subtle)',
  border: '1px solid var(--fg-danger)',
  color: 'var(--fg-danger)',
};
